import { useState, useEffect } from 'react';
import { convertSectoHHMM, convertHHMMtoSec, formatTimestampIso, getYear } from '../../../../libs.jsx';
import CareflowEntityPicker from './CareflowEntityPicker.jsx';
import CareflowChipInput from './CareflowChipInput.jsx';

// Fresh per-output config defaults (Add mode). time = Message After; for
// mobile/text/voice/email it's minutes, for pager it's seconds. repeat applies
// to mobile/pager only. entities = group ids (mobile/pager) / phone digits
// (text/voice) / emails.
const makeDefaultOutputCfg = () => ({
    mobile: { time: 0, repeatOn: false, repeatInterval: 5, entities: [] },
    text:   { time: 0, entities: [] },
    voice:  { time: 0, entities: [] },
    email:  { time: 0, entities: [] },
    pager:  { time: 0, repeatOn: false, repeatInterval: 3, entities: [] },
});

const minutesToSec = (m) => (parseInt(m, 10) || 0) * 60;

/**
 * CareflowDrawer — Add/Edit careflow settings.
 *
 * Props:
 *   open             — drawer visibility
 *   onClose          — close callback
 *   onSave           — save callback (receives formData, and oldPayload for edit)
 *   saving           — boolean, disables buttons while saving
 *   editTarget       — null for add mode, careflow data row for edit mode
 *   subTabKey        — 'alert' | 'device' | 'resident' | 'area' | 'default'
 *   eventCategories  — array of event category objects [{ event_category_id, event_category_name }]
 *   campusSettings   — campus settings object with global output flags
 *   campusId         — current campus ID
 */
const CareflowDrawer = ({ open, onClose, onSave, saving, editTarget, subTabKey, eventCategories, campusSettings, campusId, devices = [], residentGroups = [], areaGroups = [], mobileGroups = [] }) => {
    const isEdit = !!editTarget;

    const subTabLabelMap = { alert: 'Alert', device: 'Device', resident: 'Resident', area: 'Area', default: 'Default Emergency' };
    const subTabLabel = subTabLabelMap[subTabKey] || 'Alert';

    const escTypeMap = { alert: 1, device: 3, resident: 4, area: 5, default: 1 };

    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('');
    const [allDay, setAllDay] = useState(false);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [enabled, setEnabled] = useState(false);

    // Output toggles
    const [mobile, setMobile] = useState(false);
    const [text, setText] = useState(false);
    const [voice, setVoice] = useState(false);
    const [email, setEmail] = useState(false);
    const [pager, setPager] = useState(false);
    const [serial, setSerial] = useState(false);
    const [offsite, setOffsite] = useState(false);
    const [mute, setMute] = useState(false);
    const [reporting, setReporting] = useState(false);

    // Add-mode only: main assignment (references) + per-output inline config.
    const [assignmentIds, setAssignmentIds] = useState([]);
    const [outputCfg, setOutputCfg] = useState(makeDefaultOutputCfg);
    const setCfg = (key, field, value) =>
        setOutputCfg((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

    // Subtabs whose careflow has a main entity assignment (references).
    const ASSIGNMENT_CONFIG = {
        device:   { label: 'Devices',         items: devices,        idKey: 'device_id',         nameKey: 'device_name',         descKeys: ['descriptions', 'entity_name', 'manufacture_uuid'], searchKeys: ['device_name', 'descriptions', 'entity_name', 'manufacture_uuid'], placeholder: 'Search devices...', empty: 'No devices found' },
        resident: { label: 'Resident Groups', items: residentGroups, idKey: 'resident_group_id', nameKey: 'resident_group_name', descKeys: ['resident_description'], searchKeys: ['resident_group_name', 'resident_description'], placeholder: 'Search resident groups...', empty: 'No resident groups found' },
        area:     { label: 'Area Groups',     items: areaGroups,     idKey: 'area_group_id',     nameKey: 'area_group_name',     descKeys: ['area_group_description'], searchKeys: ['area_group_name', 'area_group_description'], placeholder: 'Search area groups...', empty: 'No area groups found' },
    };
    const assignmentCfg = ASSIGNMENT_CONFIG[subTabKey] || null;

    const [errors, setErrors] = useState({});

    // Filter out event_category_id === 1
    const filteredCategories = (eventCategories || []).filter((c) => c.event_category_id !== 1);

    // Reset form when drawer opens or target changes
    useEffect(() => {
        if (open) {
            // Add-mode assignment + per-output config always start fresh.
            setAssignmentIds([]);
            setOutputCfg(makeDefaultOutputCfg());
            if (editTarget) {
                // Edit mode
                setDescription(editTarget.careflow_description || editTarget.description || '');
                const isAllDay = editTarget.enable_start === 0 && editTarget.enable_end === 86400;
                setAllDay(isAllDay);
                setStartTime(isAllDay ? '00:00' : convertSectoHHMM(editTarget.enable_start));
                setEndTime(isAllDay ? '23:59' : convertSectoHHMM(editTarget.enable_end));
                setEnabled(editTarget.enabled === 1 || editTarget.enabled === true);
                setCategory(editTarget.event_category_id != null ? String(editTarget.event_category_id) : '');

                // Outputs — try nested outputs object first, then flat fields.
                // Force to false when the campus output setting is disabled.
                const o = editTarget.outputs || {};
                const cs = campusSettings || {};
                setMobile(cs.app_active ? (editTarget.mobile_enabled != null ? !!editTarget.mobile_enabled : !!(o.palmobile && o.palmobile.enabled)) : false);
                setText(cs.text_messaging_active ? (editTarget.text_enabled != null ? !!editTarget.text_enabled : !!(o.textmessages && o.textmessages.enabled)) : false);
                setVoice((cs.voice_active || cs.voice_messaging_active) ? (editTarget.voice_enabled != null ? !!editTarget.voice_enabled : !!(o.voicemessages && o.voicemessages.enabled)) : false);
                setEmail(cs.email_active ? (editTarget.email_enabled != null ? !!editTarget.email_enabled : !!(o.email && o.email.enabled)) : false);
                setPager(cs.pager_active ? (editTarget.pager_enabled != null ? !!editTarget.pager_enabled : !!(o.pager && o.pager.enabled)) : false);
                setSerial(cs.serial_active ? (editTarget.serial_enabled != null ? !!editTarget.serial_enabled : !!(o.serial && o.serial.enabled)) : false);
                setOffsite((cs.offsite_active || cs.offsite_monitoring_active) ? (editTarget.offsite_enabled != null ? !!editTarget.offsite_enabled : !!(o.offsitemonitoring && o.offsitemonitoring.enabled)) : false);
                setMute(editTarget.mute_enabled != null ? !!editTarget.mute_enabled : !!(o.mute && o.mute.enabled));
                setReporting(false);
            } else {
                // Add mode
                const isDefault = subTabKey === 'default';
                setDescription('');
                setAllDay(isDefault);
                setStartTime(isDefault ? '00:00' : '');
                setEndTime(isDefault ? '23:59' : '');
                setEnabled(false);
                setCategory(filteredCategories.length > 0 ? String(filteredCategories[0].event_category_id) : '');

                // Default output toggles to campus settings flags
                setMobile(!!(campusSettings && campusSettings.app_active));
                setText(!!(campusSettings && campusSettings.text_messaging_active));
                setVoice(!!(campusSettings && campusSettings.voice_messaging_active));
                setEmail(!!(campusSettings && campusSettings.email_active));
                setPager(!!(campusSettings && campusSettings.pager_active));
                setSerial(!!(campusSettings && campusSettings.serial_active));
                setOffsite(!!(campusSettings && campusSettings.offsite_monitoring_active));
                setMute(false);
                setReporting(false);
            }
            setErrors({});
        }
    }, [open, editTarget]);

    const descriptionPattern = /^[a-zA-Z0-9_ \-/]+$/;

    const validate = () => {
        const errs = {};

        if (!description.trim()) {
            errs.description = 'Description is required.';
        } else if (!descriptionPattern.test(description.trim())) {
            errs.description = 'Only letters, numbers, spaces, and - _ / are allowed.';
        }

        if (!allDay) {
            if (!startTime) errs.startTime = 'Start time is required.';
            if (!endTime) errs.endTime = 'End time is required.';
        }

        if (subTabKey === 'alert' && !category) {
            errs.category = 'Category is required.';
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;

        const enableStart = allDay ? 0 : convertHHMMtoSec(startTime);
        const enableEnd = allDay ? 86400 : (endTime === '23:59' ? 86400 : convertHHMMtoSec(endTime));

        if (isEdit) {
            const formData = {
                escalation_entity_id: editTarget.escalation_entity_id,
                escalation_event_id: subTabKey === 'alert' ? parseInt(category) : 1,
                careflow_id: editTarget.careflow_id,
                references: editTarget.references,
                enabled: enabled ? 1 : 0,
                enable_start: enableStart,
                enable_end: enableEnd,
                careflow_description: description.trim(),
                mobile_enabled: mobile ? 1 : 0,
                text_enabled: text ? 1 : 0,
                voice_enabled: voice ? 1 : 0,
                email_enabled: email ? 1 : 0,
                pager_enabled: pager ? 1 : 0,
                reporting_enabled: reporting ? 1 : 0,
                offsite_enabled: offsite ? 1 : 0,
                mute_enabled: mute ? 1 : 0,
                escalation_type_id: escTypeMap[subTabKey],
                campus_id: campusId,
            };

            // Build oldPayload from editTarget's original values
            const oldPayload = {
                escalation_entity_id: editTarget.escalation_entity_id,
                escalation_event_id: editTarget.escalation_event_id,
                careflow_id: editTarget.careflow_id,
                references: editTarget.references,
                enabled: editTarget.enabled,
                enable_start: editTarget.enable_start,
                enable_end: editTarget.enable_end,
                careflow_description: editTarget.careflow_description || editTarget.description || '',
                mobile_enabled: editTarget.mobile_enabled,
                text_enabled: editTarget.text_enabled,
                voice_enabled: editTarget.voice_enabled,
                email_enabled: editTarget.email_enabled,
                pager_enabled: editTarget.pager_enabled,
                reporting_enabled: editTarget.reporting_enabled,
                offsite_enabled: editTarget.offsite_enabled,
                mute_enabled: editTarget.mute_enabled,
                escalation_type_id: editTarget.escalation_type_id,
                campus_id: campusId,
            };

            onSave(formData, oldPayload);
        } else {
            // Main assignment (references) applies to device/resident/area careflows.
            const references = assignmentCfg ? assignmentIds.join(',') : '';
            const m = outputCfg.mobile;
            const t = outputCfg.text;
            const v = outputCfg.voice;
            const e = outputCfg.email;
            const p = outputCfg.pager;

            const formData = {
                careflow_description: description.trim(),
                enable_start: enableStart,
                enable_end: enableEnd,
                escalation_event_id: subTabKey === 'alert' ? parseInt(category) : 1,
                escalation_type_id: escTypeMap[subTabKey],
                references,
                frequency_id: 1,
                // Mobile — Message After (minutes → sec), repeat (minutes → sec), groups
                mobile_enabled: mobile,
                mobile_time: mobile ? minutesToSec(m.time) : 0,
                mobile_entities: mobile ? m.entities : [],
                mobile_repeat: mobile && m.repeatOn ? minutesToSec(m.repeatInterval) : 0,
                mobile_max_time: mobile ? m.repeatOn : false,
                // Text — Message After (minutes → sec), phone numbers
                text_enabled: text,
                text_time: text ? minutesToSec(t.time) : 0,
                text_entities: text ? t.entities : [],
                // Voice — Message After (minutes → sec), phone numbers
                voice_enabled: voice,
                voice_time: voice ? minutesToSec(v.time) : 0,
                voice_entities: voice ? v.entities : [],
                // Email — Message After (minutes → sec), emails
                email_enabled: email,
                email_time: email ? minutesToSec(e.time) : 0,
                email_entities: email ? e.entities : [],
                // Pager — Message After (seconds, direct), repeat (minutes → sec), groups
                pager_enabled: pager,
                pager_time: pager ? (parseInt(p.time, 10) || 0) : 0,
                pager_entities: pager ? p.entities : [],
                pager_repeat: pager && p.repeatOn ? minutesToSec(p.repeatInterval) : 0,
                pager_max_time: pager ? p.repeatOn : false,
                offsite_time: offsite ? 0 : 0,
                offsite_enabled: offsite,
                offsite_entities: [],
                serial_enabled: serial,
                serial_repeat: 0,
                serial_max_time: false,
                report_enabled: reporting,
                mute_enabled: mute,
                ignore_until: formatTimestampIso(getYear(0)[0]),
                campus_id: campusId,
            };

            onSave(formData);
        }
    };

    const clearError = (field) => {
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    // Add-mode inline config panel for an enabled output. Mobile/Pager get a
    // repeat control + group picker; Text/Voice a phone list; Email an email list.
    const renderOutputConfig = (key) => {
        const cfg = outputCfg[key];
        const isPager = key === 'pager';
        const hasRepeat = key === 'mobile' || key === 'pager';
        const isGroups = key === 'mobile' || key === 'pager';
        const isPhones = key === 'text' || key === 'voice';
        const isEmails = key === 'email';
        return (
            <div className="ws-output-config">
                <div className="dp-drawer__field">
                    <label className="dp-drawer__label">{isPager ? 'Message After (seconds)' : 'Message After (minutes)'}</label>
                    <input
                        className="dp-drawer__input"
                        type="number"
                        min="0"
                        max={isPager ? 7200 : 120}
                        value={cfg.time}
                        onChange={(ev) => setCfg(key, 'time', ev.target.value)}
                    />
                    <p className="ws-output-config__hint">
                        0 = Immediate Send.{isPager ? ' 1–7200 = seconds' : ' 1–120 = minutes'} from alarm generation.
                    </p>
                </div>

                {hasRepeat ? (
                    <>
                        <div className="ws-toggle-row">
                            <span className="ws-toggle-label">Send Repeats</span>
                            <label className="dp-toggle">
                                <input type="checkbox" checked={cfg.repeatOn} onChange={(ev) => setCfg(key, 'repeatOn', ev.target.checked)} />
                                <span className="dp-toggle__track" />
                            </label>
                        </div>
                        {cfg.repeatOn ? (
                            <div className="dp-drawer__field">
                                <label className="dp-drawer__label">Repeat Interval (minutes)</label>
                                <input
                                    className="dp-drawer__input"
                                    type="number"
                                    min="2"
                                    max="60"
                                    value={cfg.repeatInterval}
                                    onChange={(ev) => setCfg(key, 'repeatInterval', ev.target.value)}
                                />
                            </div>
                        ) : null}
                    </>
                ) : null}

                {isGroups ? (
                    <CareflowEntityPicker
                        label="Groups"
                        items={mobileGroups}
                        idKey="group_id"
                        nameKey="group_name"
                        descKeys={isPager ? ['capcode'] : []}
                        searchKeys={['group_name', 'capcode']}
                        selectedIds={cfg.entities}
                        onChange={(ids) => setCfg(key, 'entities', ids)}
                        searchPlaceholder="Search groups..."
                        emptyText="No groups found"
                    />
                ) : null}
                {isPhones ? (
                    <CareflowChipInput kind="phone" values={cfg.entities} onChange={(vals) => setCfg(key, 'entities', vals)} />
                ) : null}
                {isEmails ? (
                    <CareflowChipInput kind="email" values={cfg.entities} onChange={(vals) => setCfg(key, 'entities', vals)} />
                ) : null}
            </div>
        );
    };

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer${open ? ' is-open' : ''}`}>
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">
                        {isEdit ? `Edit ${subTabLabel} Careflow` : `Add ${subTabLabel} Careflow`}
                    </h2>
                    <button className="dp-drawer__close" onClick={onClose}>
                        <i className="fe fe-x" />
                    </button>
                </div>

                <div className="dp-drawer__body">
                    {/* Description */}
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            Description <span className="dp-drawer__required">*</span>
                        </label>
                        <input
                            className={`dp-drawer__input${errors.description ? ' is-invalid' : ''}`}
                            type="text"
                            value={description}
                            placeholder="Enter careflow description"
                            onChange={(e) => { setDescription(e.target.value); clearError('description'); }}
                        />
                        {errors.description ? <p className="dp-drawer__error">{errors.description}</p> : null}
                    </div>

                    {/* Category (alert subtab only) */}
                    {subTabKey === 'alert' ? (
                        <div className="dp-drawer__field">
                            <label className="dp-drawer__label">
                                Category <span className="dp-drawer__required">*</span>
                            </label>
                            <select
                                className={`dp-drawer__input${errors.category ? ' is-invalid' : ''}`}
                                value={category}
                                onChange={(e) => { setCategory(e.target.value); clearError('category'); }}
                            >
                                {filteredCategories.map((cat) => (
                                    <option key={cat.event_category_id} value={cat.event_category_id}>
                                        {cat.event_category_name}
                                    </option>
                                ))}
                            </select>
                            {errors.category ? <p className="dp-drawer__error">{errors.category}</p> : null}
                        </div>
                    ) : null}

                    {/* All Day toggle */}
                    <div className="ws-toggle-row">
                        <span className="ws-toggle-label">All Day</span>
                        <label className="dp-toggle">
                            <input
                                type="checkbox"
                                checked={allDay}
                                disabled={subTabKey === 'default'}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setAllDay(checked);
                                    if (checked) {
                                        setStartTime('00:00');
                                        setEndTime('23:59');
                                        clearError('startTime');
                                        clearError('endTime');
                                    } else {
                                        setStartTime('');
                                        setEndTime('');
                                    }
                                }}
                            />
                            <span className="dp-toggle__track" />
                        </label>
                    </div>

                    {/* Active Start / Active End */}
                    <div className="ws-time-row">
                        <div className="dp-drawer__field">
                            <label className="dp-drawer__label">
                                Active Start {!allDay ? <span className="dp-drawer__required">*</span> : null}
                            </label>
                            <input
                                className={`dp-drawer__input${errors.startTime ? ' is-invalid' : ''}`}
                                type="time"
                                step="300"
                                value={startTime}
                                disabled={allDay}
                                onChange={(e) => { setStartTime(e.target.value); clearError('startTime'); }}
                            />
                            {errors.startTime ? <p className="dp-drawer__error">{errors.startTime}</p> : null}
                        </div>
                        <div className="dp-drawer__field">
                            <label className="dp-drawer__label">
                                Active End {!allDay ? <span className="dp-drawer__required">*</span> : null}
                            </label>
                            <input
                                className={`dp-drawer__input${errors.endTime ? ' is-invalid' : ''}`}
                                type="time"
                                step="300"
                                value={endTime}
                                disabled={allDay}
                                onChange={(e) => { setEndTime(e.target.value); clearError('endTime'); }}
                            />
                            {errors.endTime ? <p className="dp-drawer__error">{errors.endTime}</p> : null}
                        </div>
                    </div>

                    {/* Status dropdown (edit mode only) */}
                    {isEdit ? (
                        <div className="dp-drawer__field">
                            <label className="dp-drawer__label">Status</label>
                            <select
                                className="dp-drawer__input"
                                value={enabled ? 'active' : 'inactive'}
                                onChange={(e) => setEnabled(e.target.value === 'active')}
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    ) : null}

                    {/* Assignment section — main careflow entities (Add mode only).
                        Device → devices, Resident → resident groups, Area → area groups.
                        Alert / Default careflows apply by event category, no assignment. */}
                    {!isEdit && assignmentCfg ? (
                        <div style={{ borderTop: '1px solid var(--color-border, #e5e7eb)', margin: 'var(--space-4, 1rem) 0', paddingTop: 'var(--space-3, 0.75rem)' }}>
                            <h4 style={{ fontSize: 'var(--font-size-sm, 0.875rem)', fontWeight: 600, color: 'var(--color-text-secondary, #374151)', marginBottom: 'var(--space-3, 0.75rem)' }}>Assignment</h4>
                            <CareflowEntityPicker
                                label={assignmentCfg.label}
                                items={assignmentCfg.items}
                                idKey={assignmentCfg.idKey}
                                nameKey={assignmentCfg.nameKey}
                                descKeys={assignmentCfg.descKeys}
                                searchKeys={assignmentCfg.searchKeys}
                                selectedIds={assignmentIds}
                                onChange={setAssignmentIds}
                                searchPlaceholder={assignmentCfg.placeholder}
                                emptyText={assignmentCfg.empty}
                            />
                        </div>
                    ) : null}

                    {/* Output Settings section — only show toggles for outputs enabled in campus Output Settings */}
                    <div style={{ borderTop: '1px solid var(--color-border, #e5e7eb)', margin: 'var(--space-4, 1rem) 0', paddingTop: 'var(--space-3, 0.75rem)' }}>
                        <h4 style={{ fontSize: 'var(--font-size-sm, 0.875rem)', fontWeight: 600, color: 'var(--color-text-secondary, #374151)', marginBottom: 'var(--space-3, 0.75rem)' }}>Output Settings</h4>
                    </div>

                    {/* PalCare Mobile */}
                    {campusSettings?.app_active ? (
                        <>
                            <div className={`ws-toggle-row ${!isEdit ? " ws-expandable" : ""}`}>
                                <span className="ws-toggle-label">PalCare Mobile</span>
                                <label className="dp-toggle">
                                    <input type="checkbox" checked={mobile} onChange={(e) => setMobile(e.target.checked)} />
                                    <span className="dp-toggle__track" />
                                </label>
                            </div>
                            {!isEdit && mobile ? renderOutputConfig('mobile') : null}
                        </>
                    ) : null}

                    {/* Text Messaging */}
                    {campusSettings?.text_messaging_active ? (
                        <>
                            <div className={`ws-toggle-row ${!isEdit ? " ws-expandable" : ""}`}>
                                <span className="ws-toggle-label">Text Messaging</span>
                                <label className="dp-toggle">
                                    <input type="checkbox" checked={text} onChange={(e) => setText(e.target.checked)} />
                                    <span className="dp-toggle__track" />
                                </label>
                            </div>
                            {!isEdit && text ? renderOutputConfig('text') : null}
                        </>
                    ) : null}

                    {/* Voice Calls */}
                    {(campusSettings?.voice_active || campusSettings?.voice_messaging_active) ? (
                        <>
                            <div className={`ws-toggle-row ${!isEdit ? " ws-expandable" : ""}`}>
                                <span className="ws-toggle-label">Voice Calls</span>
                                <label className="dp-toggle">
                                    <input type="checkbox" checked={voice} onChange={(e) => setVoice(e.target.checked)} />
                                    <span className="dp-toggle__track" />
                                </label>
                            </div>
                            {!isEdit && voice ? renderOutputConfig('voice') : null}
                        </>
                    ) : null}

                    {/* Email */}
                    {campusSettings?.email_active ? (
                        <>
                            <div className={`ws-toggle-row ${!isEdit ? " ws-expandable" : ""}`}>
                                <span className="ws-toggle-label">Email</span>
                                <label className="dp-toggle">
                                    <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
                                    <span className="dp-toggle__track" />
                                </label>
                            </div>
                            {!isEdit && email ? renderOutputConfig('email') : null}
                        </>
                    ) : null}

                    {/* Pager */}
                    {campusSettings?.pager_active ? (
                        <>
                            <div className={`ws-toggle-row ${!isEdit ? " ws-expandable" : ""}`}>
                                <span className="ws-toggle-label">Pager</span>
                                <label className="dp-toggle">
                                    <input type="checkbox" checked={pager} onChange={(e) => setPager(e.target.checked)} />
                                    <span className="dp-toggle__track" />
                                </label>
                            </div>
                            {!isEdit && pager ? renderOutputConfig('pager') : null}
                        </>
                    ) : null}

                    {/* Serial Out */}
                    {campusSettings?.serial_active ? (
                        <div className="ws-toggle-row">
                            <span className="ws-toggle-label">Serial Out</span>
                            <label className="dp-toggle">
                                <input type="checkbox" checked={serial} onChange={(e) => setSerial(e.target.checked)} />
                                <span className="dp-toggle__track" />
                            </label>
                        </div>
                    ) : null}

                    {/* Offsite Monitoring */}
                    {(campusSettings?.offsite_active || campusSettings?.offsite_monitoring_active) ? (
                        <div className="ws-toggle-row">
                            <span className="ws-toggle-label">Offsite Monitoring</span>
                            <label className="dp-toggle">
                                <input type="checkbox" checked={offsite} onChange={(e) => setOffsite(e.target.checked)} />
                                <span className="dp-toggle__track" />
                            </label>
                        </div>
                    ) : null}

                    {/* Mute — always visible regardless of Output Settings */}
                    <div className="ws-toggle-row">
                        <span className="ws-toggle-label">Mute</span>
                        <label className="dp-toggle">
                            <input type="checkbox" checked={mute} onChange={(e) => setMute(e.target.checked)} />
                            <span className="dp-toggle__track" />
                        </label>
                    </div>

                    {/* Reporting — hidden, defaulted to off (not currently utilized) */}
                </div>

                <div className="dp-drawer__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                        {saving
                            ? (isEdit ? 'Saving...' : 'Adding...')
                            : (isEdit ? 'Save Changes' : 'Add Careflow')}
                    </button>
                </div>
            </div>
        </>
    );
};

export default CareflowDrawer;
